import { useState, useEffect } from 'react';
import { cachedFetchJSON, GEO_TTL } from '../utils/fetchCache';

// CDC/ATSDR Social Vulnerability Index — hosted on ArcGIS Online (ATSDR account)
// Available years: 2018, 2020, 2022 (biennial releases)
// TODO: Verify service names at:
//   https://services1.arcgis.com/RLQu0rK7h4kbsBq5/arcgis/rest/services
// If these 400, the org ID or service name may have changed; check ArcGIS Living Atlas
// for "CDC Social Vulnerability Index" and update SVI_BASE + service name accordingly.
const SVI_BASE = 'https://services1.arcgis.com/RLQu0rK7h4kbsBq5/arcgis/rest/services';
const SVI_TRACT_LAYER = 0;   // census tract layer
const SVI_COUNTY_LAYER = 1;  // county layer

function sviQueryUrl(year, layer) {
  return `${SVI_BASE}/CDC_Social_Vulnerability_Index_${year}/FeatureServer/${layer}/query`;
}

// SVI percentiles range 0.00–1.00; multiply × 100 for display. -999 = suppressed → null.
function parseSvi(raw) {
  const v = parseFloat(raw);
  if (isNaN(v) || v === -999) return null;
  return Math.round(v * 1000) / 10; // → 0.0–100.0
}

async function fetchSviTracts(year, stateFips, countyFips) {
  const where = `FIPS LIKE '${stateFips}${countyFips}%'`;
  const url =
    `${sviQueryUrl(year, SVI_TRACT_LAYER)}` +
    `?where=${encodeURIComponent(where)}` +
    `&outFields=FIPS,RPL_THEMES,RPL_THEME1,RPL_THEME2,RPL_THEME3,RPL_THEME4` +
    `&f=json&resultRecordCount=2000`;
  const data = await cachedFetchJSON(url, GEO_TTL);
  return (data?.features ?? []).map(f => ({
    fips: String(f.attributes?.FIPS ?? ''),
    RPL_THEMES: f.attributes?.RPL_THEMES,
    RPL_THEME1: f.attributes?.RPL_THEME1,
    RPL_THEME2: f.attributes?.RPL_THEME2,
    RPL_THEME3: f.attributes?.RPL_THEME3,
    RPL_THEME4: f.attributes?.RPL_THEME4
  }));
}

async function fetchSviCounty(year, fips5) {
  const where = `FIPS='${fips5}'`;
  const url =
    `${sviQueryUrl(year, SVI_COUNTY_LAYER)}` +
    `?where=${encodeURIComponent(where)}` +
    `&outFields=FIPS,RPL_THEMES,RPL_THEME1,RPL_THEME2,RPL_THEME3,RPL_THEME4` +
    `&f=json`;
  const data = await cachedFetchJSON(url, GEO_TTL);
  return data?.features?.[0]?.attributes ?? null;
}

// Fetches all county-level SVI for the state (for state choropleth toggle)
async function fetchSviStateCounties(year, stateFips) {
  const where = `FIPS LIKE '${stateFips}%'`;
  const url =
    `${sviQueryUrl(year, SVI_COUNTY_LAYER)}` +
    `?where=${encodeURIComponent(where)}` +
    `&outFields=FIPS,RPL_THEMES,RPL_THEME1,RPL_THEME2,RPL_THEME3,RPL_THEME4` +
    `&f=json&resultRecordCount=1000`;
  try {
    const data = await cachedFetchJSON(url, GEO_TTL);
    return data?.features ?? [];
  } catch (_) { return []; }
}

/**
 * Hook: fetches CDC/ATSDR Social Vulnerability Index (SVI) data.
 * SVI is available for 2018, 2020, 2022. Values are percentiles (0–100).
 *
 * Returns standard IndicatorData Map with geography keyed by 11-digit tract FIPS.
 */
export function useSVIData(config, overrideIndicators, geographyOverride) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const indicators = (overrideIndicators ?? config.indicators).filter(i => i.source === 'svi');
    if (indicators.length === 0) { setData(new Map()); setLoading(false); return; }

    let cancelled = false;

    async function load() {
      setLoading(true);
      setError(null);
      try {
        const { state: stateFips, county: countyFips } = config.org.geography;
        const fips5 = `${stateFips}${countyFips}`;
        const results = new Map();

        await Promise.all(indicators.map(async indicator => {
          const years = indicator.years ?? [2018, 2020, 2022];
          const latestYear = years[years.length - 1];
          const field = indicator.sviField ?? 'RPL_THEMES';

          const yearData = await Promise.all(years.map(async year => {
            const [tracts, county] = await Promise.all([
              fetchSviTracts(year, stateFips, countyFips).catch(() => []),
              fetchSviCounty(year, fips5).catch(() => null)
            ]);
            const geography = tracts
              .filter(t => t.fips?.length === 11)
              .map(t => ({ geoid: t.fips, name: `Tract ${t.fips.slice(-6)}`, value: parseSvi(t[field]) }))
              .filter(g => g.value !== null);
            const countyVal = county ? parseSvi(county[field]) : null;
            return { year, geography, countyVal };
          }));

          const geographyByYear = new Map(yearData.map(({ year, geography }) => [year, geography]));
          const trend = yearData
            .filter(d => d.countyVal !== null)
            .map(({ year, countyVal }) => ({ year, value: countyVal }));
          const geography = geographyByYear.get(latestYear) ?? [];
          const countyValue = trend.find(p => p.year === latestYear)?.value ?? null;

          // State-level: average across all counties in the state
          let stateTrend = [];
          let stateGeography = [];
          let stateValue = null;
          try {
            const stateYearData = await Promise.all(years.map(async year => {
              const features = await fetchSviStateCounties(year, stateFips);
              const entries = features
                .map(f => ({ fips: String(f.attributes?.FIPS ?? ''), value: parseSvi(f.attributes?.[field]) }))
                .filter(e => e.fips.length === 5 && e.value !== null);
              if (year === latestYear) {
                stateGeography = entries.map(e => ({ geoid: e.fips, value: e.value }));
              }
              const avg = entries.length
                ? entries.reduce((s, e) => s + e.value, 0) / entries.length
                : null;
              return avg !== null ? { year, value: avg } : null;
            }));
            stateTrend = stateYearData.filter(Boolean);
            stateValue = stateTrend.find(p => p.year === latestYear)?.value ?? null;
          } catch (_) {}

          results.set(indicator.id, {
            indicatorId: indicator.id,
            label: indicator.label,
            unit: indicator.unit,
            geography,
            geographyByYear,
            stateGeography,
            trend,
            stateTrend,
            countyValue,
            stateValue
          });
        }));

        if (!cancelled) { setData(results); setLoading(false); }
      } catch (err) {
        if (!cancelled) { setError(`SVI data failed to load: ${err.message}`); setLoading(false); }
      }
    }

    load();
    return () => { cancelled = true; };
  }, [config, overrideIndicators, geographyOverride]); // eslint-disable-line react-hooks/exhaustive-deps

  return { data, loading, error };
}
